import React, { useState, useEffect } from 'react';
import { Grid, Paper, Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList, LineChart, Line } from 'recharts';
import useApiManager from '../../utils/Api/apiManager';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const revenueData = {
    thisMonth: 0,
    lastYearSamePeriod: 411.82,
};

const dealsData = [
    { year: '2021', amount: 20269 },
    { year: '2022', amount: 697763 },
    { year: '2023', amount: 2827496.34 },
    { year: '2024', amount: 2827496.34 },
    { year: '2025', amount: 2494243 },
];

const revenueByUsers = [
    { name: 'Armir', amount: 551767, change: 100 },
    { name: 'Valdet', amount: 264694, change: -79.3 },
];

const revenueTrendData = [
    { month: 'Jan', revenue: 100000 },
    { month: 'Feb', revenue: 120000 },
    { month: 'Mar', revenue: 90000 },
    { month: 'Apr', revenue: 150000 },
    { month: 'May', revenue: 130000 },
    { month: 'Jun', revenue: 170000 },
    { month: 'Jul', revenue: 160000 },
    { month: 'Mar', revenue: 90000 },
    { month: 'Apr', revenue: 150000 },
    { month: 'May', revenue: 130000 },
    { month: 'Jun', revenue: 170000 },
    { month: 'Jul', revenue: 160000 },
];


const AnalyticsPage = () => {
    const { get } = useApiManager();
    const [revenueThisMonth, setRevenueThisMonth] = useState({});
    const [dealsThisMonth, setDealsThisMonth] = useState({});
    const [dealsByYear, setDealsByYear] = useState([]);
    const [revenueTrend, setRevenueTrend] = useState([]);
    const [revenueByOwnersYear, setRevenueByOwnersYear] = useState([]);
    const [revenueByOwnersMonth, setRevenueByOwnersMonth] = useState([]);
    const [tab, setTab] = useState(0);

    const revenueByOwners = tab === 0 ? revenueByOwnersYear : revenueByOwnersMonth;

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const fetchRevenueThisMonth = async () => {
        //setLoading(true);
        try {
            const data = await get(`/analytics/deals/this-month`);
            setRevenueThisMonth(data);
        } catch (error) {
            console.error('Error fetching deals this month:', error);
        } finally {
            //setLoading(false);
        }
    }

    const fetchDealsThisMonth = async () => {
        //setLoading(true);
        try {
            const data = await get(`/analytics/deals/count-this-month`);
            setDealsThisMonth(data);
        } catch (error) {
            console.error('Error fetching deals this month:', error);
        } finally {
            //setLoading(false);
        }
    }

    const fetchDealsByYear = async () => {
        //setLoading(true);
        try {
            const data = await get(`/analytics/deals/by-year`);
            setDealsByYear(data);
        } catch (error) {
            console.error('Error fetching deals by year:', error);
        } finally {
            //setLoading(false);
        }
    }

    const fetchRevenueTrend = async () => {
        //setLoading(true);
        try {
            const data = await get(`/analytics/deals/last-12-months`);
            setRevenueTrend(data);
        } catch (error) {
            console.error('Error fetching revenue trend:', error);
        } finally {
            //setLoading(false);
        }
    }

    const fetchRevenueByOwnersYear = async () => {
        //setLoading(true);
        try {
            const data = await get(`/analytics/deals/by-owners`);
            setRevenueByOwnersYear(data);
        } catch (error) {
            console.error('Error fetching revenue by owners:', error);
        } finally {
            //setLoading(false);
        }
    }

    const fetchRevenueByOwnersMonth = async () => {
        //setLoading(true);
        try {
            const data = await get(`/analytics/deals/by-owners-this-month`);
            setRevenueByOwnersMonth(data);
        } catch (error) {
            console.error('Error fetching revenue by owners:', error);
        } finally {
            //setLoading(false);
        }
    }

    useEffect(() => {
        document.title = "Analytics Page - iLead CRM";
        fetchRevenueThisMonth();
        fetchDealsThisMonth();
        fetchDealsByYear();
        fetchRevenueTrend();
        fetchRevenueByOwnersYear();
        fetchRevenueByOwnersMonth();
    }, []);

    return (
        <Box p={2}>
            {/* Row 1 */}
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Typography variant="h6">Revenue This Month</Typography>

                        <Box display="flex" alignItems="baseline">
                            <Typography variant="h4">
                                ${revenueThisMonth.currentMonthAmount?.toLocaleString('en-US') ?? '0'}
                            </Typography>
                            <Box display="flex" alignItems="center" sx={{ ml: 1 }}>
                                {revenueThisMonth.percentageChange >= 0 ? (
                                    <ArrowDropUpIcon fontSize="small" color="success" />
                                ) : (
                                    <ArrowDropDownIcon fontSize="small" color="error" />
                                )}
                                <Typography
                                    variant="body2"
                                    color={revenueThisMonth.percentageChange >= 0 ? 'success.main' : 'error'}
                                >
                                    {Math.abs(revenueThisMonth.percentageChange) ?? 0}%
                                </Typography>
                            </Box>
                        </Box>

                        <Typography variant="body2" color="text.secondary">
                            Same Period Last Year: ${revenueThisMonth.lastYearSameMonthAmount?.toLocaleString('en-US') ?? '0'}
                        </Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2 }}>
                        <Typography variant="h6">Deals Closed Won This Month</Typography>
                        <Typography variant="h4">{dealsThisMonth.count ?? 0}</Typography>
                        <Typography variant="body2" color="text.secondary">Last Deal Updated {dealsThisMonth.lastClosedAgo ?? '-'}</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Row 2 */}
            <Grid container spacing={2} mt={1}>
                <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2, height: 400 }}>
                        <Typography variant="h6" gutterBottom>Deals Over the Last 5 Years</Typography>
                        <ResponsiveContainer width="100%" height="90%">
                            <BarChart data={dealsByYear} margin={{ top: 30, left: 10 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="year" />
                                <YAxis />
                                <Tooltip formatter={(value) => `$${value?.toLocaleString('en-US') ?? '0'}`} />
                                <Bar dataKey="amount" fill="#2196f3">
                                    <LabelList
                                        dataKey="amount"
                                        position="top"
                                        formatter={(value) => `$${value?.toLocaleString('en-US') ?? '0'}`}
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Paper elevation={1} sx={{ p: 2, height: 400 }}>
                        <Typography variant="h6" gutterBottom>Revenue Trend Last 12 Months</Typography>
                        <ResponsiveContainer width="100%" height="90%">
                            <LineChart data={revenueTrend}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="monthName" />
                                <YAxis />
                                <Tooltip formatter={(value) => `$${value?.toLocaleString('en-US') ?? '0'}`} />
                                <Line type="monotone" dataKey="amount" stroke="#4caf50" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>

            {/* Row 3 */}
            <Box mt={3}>
                <Paper elevation={1} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>Revenue by Users</Typography>
                    {/* Tabs for Year / Month */}
                    <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
                        <Tab label="This Year" />
                        <Tab label="This Month" />
                    </Tabs>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>User</TableCell>
                                    <TableCell>Revenue</TableCell>
                                    <TableCell>Change</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {revenueByOwners.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{row.ownerName}</TableCell>
                                        <TableCell>${row.currentYearAmount?.toLocaleString('en-US') ?? '0'}</TableCell>
                                        <TableCell sx={{ color: row.percentageChange >= 0 ? 'green' : 'red' }}>
                                            {row.percentageChange >= 0 ? '▲' : '▼'} {Math.abs(row.percentageChange) ?? 0}%
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </Box>
    );
};

export default AnalyticsPage;
